import { Fragment } from 'react'
import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/solid'

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export const MultiSelectInput = ({
    options = [],
    value = [],
    label = '',
    placeholder = '',
    className = '',
    buttonClassName = '',
    required = false,
    onChange = () => {}
}) => {
    // Callers can share one piece of form state between this multi-select and a
    // single-select variant (e.g. the dispatch-schedule form: an array while
    // creating, a lone option while editing). Normalize instead of trusting the
    // shape — an object/null here used to throw and blank the whole page.
    const selected = Array.isArray(value) ? value : value ? [value] : [];

    const displayText = selected.length === 0
        ? placeholder
        : selected.length === 1
            ? selected[0].name
            : `${selected[0].name} (+${selected.length - 1})`;

    const handleChange = (newSelection) => {
        onChange(newSelection);
    };

    return (
        <div className={className}>
            <Listbox value={selected} onChange={handleChange} by={(a, b) => a.id === b.id} multiple>
                {({ open }) => (
                    <>
                        {label && (
                            <Listbox.Label className="block text-sm font-medium text-gray-700">
                                {label}{required && <span className="text-red-500"> *</span>}
                            </Listbox.Label>
                        )}
                        <div className="relative mt-1">
                            <Listbox.Button
                                className={classNames(
                                    'relative w-full bg-white pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm shadow-sm border rounded-md',
                                    required && selected.length === 0 ? 'border-red-400' : 'border-gray-400',
                                    buttonClassName
                                )}
                            >
                                <span className={classNames('block truncate', selected.length === 0 && 'text-gray-400')}>
                                    {displayText || <span>&nbsp;</span>}
                                </span>
                                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                    <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </span>
                            </Listbox.Button>

                            <Transition
                                show={open}
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                                <Listbox.Options
                                    static
                                    className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-80 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
                                >
                                    {options.map((option) => {
                                        const isSelected = selected.some(v => v.id === option.id);
                                        return (
                                            <Listbox.Option
                                                key={option.id + option.name}
                                                className={({ active }) =>
                                                    classNames(
                                                        active ? 'text-white bg-indigo-600' : 'text-gray-900',
                                                        'cursor-default select-none relative py-2 pl-8 pr-2'
                                                    )
                                                }
                                                value={option}
                                            >
                                                {({ active }) => (
                                                    <>
                                                        <span className={classNames(isSelected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                                                            {option.name}
                                                        </span>
                                                        {isSelected && (
                                                            <span
                                                                className={classNames(
                                                                    active ? 'text-white' : 'text-indigo-600',
                                                                    'absolute inset-y-0 left-0 flex items-center pl-1.5'
                                                                )}
                                                            >
                                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </Listbox.Option>
                                        );
                                    })}
                                </Listbox.Options>
                            </Transition>
                        </div>
                    </>
                )}
            </Listbox>
        </div>
    )
}
